import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon } from '@radix-ui/react-icons';
import { Button, Spinner } from '../../components';
import { getNotificationErrorMessage } from '../../services/notificationService';
import { useNotificationStore } from '../../store/notificationStore';
import {
  NotificationData,
  NotificationItem,
  NotificationTab,
} from '../../types/notification';
import { NotificationCard } from './components/NotificationCard';
import './NotificationsList.scss';

const TABS: { key: NotificationTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'matches', label: 'Matches' },
];

const resolveNotificationNavigation = (
  data: NotificationData
): string | { pathname: string; state?: { openTaskId: string } } | null => {
  const kanbanPath = typeof data.kanbanPath === 'string' ? data.kanbanPath : undefined;
  if (kanbanPath) {
    try {
      const url = new URL(kanbanPath, window.location.origin);
      const taskId = url.searchParams.get('taskId');
      if (taskId) {
        return { pathname: '/kanban', state: { openTaskId: taskId } };
      }
      return '/kanban';
    } catch {
      return '/kanban';
    }
  }

  const reviewPath = typeof data.reviewPath === 'string' ? data.reviewPath : undefined;
  if (reviewPath) {
    try {
      const url = new URL(reviewPath, window.location.origin);
      const targetUserId = url.searchParams.get('targetUserId');
      if (targetUserId) {
        return `/users/${targetUserId}`;
      }
    } catch {
      return null;
    }
  }

  return null;
};

export const NotificationsList: React.FC = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    currentPage,
    totalPages,
    isLoading,
    isLoadingMore,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAllAsRead,
    markAsRead,
  } = useNotificationStore();

  const [activeTab, setActiveTab] = useState<NotificationTab>('all');
  const [actionError, setActionError] = useState<string | null>(null);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  useEffect(() => {
    void fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    void fetchNotifications({ tab: activeTab, page: 1 });
  }, [activeTab, fetchNotifications]);

  const handleMarkAllRead = useCallback(async () => {
    setActionError(null);
    setIsMarkingAllRead(true);
    try {
      await markAllAsRead();
    } catch (error) {
      setActionError(getNotificationErrorMessage(error, 'Failed to mark notifications as read.'));
    } finally {
      setIsMarkingAllRead(false);
    }
  }, [markAllAsRead]);

  const handleNotificationClick = useCallback(
    async (notification: NotificationItem) => {
      setActionError(null);

      if (notification.isNew) {
        try {
          await markAsRead(notification.notificationId);
        } catch (error) {
          setActionError(
            getNotificationErrorMessage(error, 'Failed to mark notification as read.')
          );
          return;
        }
      }

      const destination = resolveNotificationNavigation(notification.data);
      if (!destination) return;

      if (typeof destination === 'string') {
        navigate(destination);
        return;
      }

      navigate(destination.pathname, { state: destination.state });
    },
    [markAsRead, navigate]
  );

  const handleLoadMore = useCallback(() => {
    if (currentPage >= totalPages || isLoadingMore) return;
    void fetchNotifications({ tab: activeTab, page: currentPage + 1, append: true });
  }, [activeTab, currentPage, fetchNotifications, isLoadingMore, totalPages]);

  const handleRetry = useCallback(() => {
    void fetchNotifications({ tab: activeTab, page: 1 });
  }, [activeTab, fetchNotifications]);

  return (
    <div className="notifications-page">
      <header className="notifications-page__header">
        <div>
          <h1>Notifications</h1>
          <p>Stay updated with your latest CRM activities and lead status.</p>
        </div>
        <button
          type="button"
          className="notifications-page__mark-read"
          onClick={() => void handleMarkAllRead()}
          disabled={unreadCount === 0 || isMarkingAllRead}
        >
          {isMarkingAllRead ? <Spinner size="small" /> : <CheckIcon aria-hidden="true" />}
          Mark All as Read
        </button>
      </header>

      <nav className="notifications-page__tabs" aria-label="Notification filters">
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            className={`notifications-page__tab ${activeTab === tab.key ? 'notifications-page__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {actionError && (
        <div className="notifications-page__feedback notifications-page__feedback--error" role="alert">
          {actionError}
        </div>
      )}

      <div className="notifications-page__list">
        {isLoading ? (
          <div className="notifications-page__loading">
            <Spinner size="medium" />
            <p>Loading notifications...</p>
          </div>
        ) : error ? (
          <div className="notifications-page__error">
            <p>{error}</p>
            <Button type="button" onClick={handleRetry}>
              Retry
            </Button>
          </div>
        ) : notifications.length === 0 ? (
          <p className="notifications-page__empty">No notifications in this view.</p>
        ) : (
          notifications.map(notification => (
            <NotificationCard
              key={notification.notificationId}
              notification={notification}
              onClick={notification => void handleNotificationClick(notification)}
            />
          ))
        )}

        {!isLoading && !error && currentPage < totalPages && (
          <button
            type="button"
            className="notifications-page__load-more"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading...' : 'Load More Notifications'}
          </button>
        )}
      </div>
    </div>
  );
};
